import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { socket } from "./appContext";
import { API_BASE_URL } from "../apiConfig";
import appApi from "../services/appApi";

const NotificationContext = createContext({
  unreadCount: 0,
  clearUnreadCount: () => {},
  refetchUnreadCount: () => {},
  refetchTick: 0,
});

export function useNotifications() {
  return useContext(NotificationContext);
}

export function NotificationProvider({ children }) {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [unreadCount, setUnreadCount] = useState(0);
  // A simple counter that NotificationsPage watches to know when to refetch
  const [refetchTick, setRefetchTick] = useState(0);

  // ── Fetch unread count from the API ───────────────────────────────────────
  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;

    try {
      const token = localStorage.getItem("userToken");
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const json = await response.json();
      if (json.success) {
        setUnreadCount(json.count);
      }
    } catch (error) {
      console.error("Error fetching unread notification count:", error);
    }
  }, [user]);

  // Fetch initial count when user logs in / page loads
  useEffect(() => {
    if (user) {
      fetchUnreadCount();
    } else {
      setUnreadCount(0);
    }
  }, [user, fetchUnreadCount]);

  // ── Listen for live Socket.IO notifications ───────────────────────────────
  useEffect(() => {
    if (!user) return;

    const handleNewNotification = () => {
      // 1. Increment badge count instantly
      setUnreadCount((prev) => prev + 1);

      // 2. Invalidate the RTK Query notification list cache so the list
      //    re-fetches live without needing a manual page refresh.
      dispatch(appApi.util.invalidateTags([{ type: "Notification", id: "LIST" }]));

      // 3. Bump the tick so NotificationsPage can also trigger a manual refetch
      //    if the user already has it open.
      setRefetchTick((prev) => prev + 1);
    };

    socket.on("new_notification", handleNewNotification);

    return () => {
      socket.off("new_notification", handleNewNotification);
    };
  }, [user, dispatch]);

  // ── Mark all as read + clear badge (called when NotificationsPage mounts) ──
  const clearUnreadCount = useCallback(async () => {
    if (unreadCount === 0) return; // No-op if already clear

    // Optimistic UI: clear badge immediately
    setUnreadCount(0);

    try {
      const token = localStorage.getItem("userToken");
      if (!token) return;

      await fetch(`${API_BASE_URL}/api/notifications/mark-all-read`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      // Re-fetch actual count if the request failed
      fetchUnreadCount();
    }
  }, [unreadCount, fetchUnreadCount]);

  return (
    <NotificationContext.Provider
      value={{
        unreadCount,
        clearUnreadCount,
        refetchUnreadCount: fetchUnreadCount,
        refetchTick,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}
